/**
 * Builds the frozen request a connector target replacement runs against, from
 * the loose values the host and the configuration store expose.
 */

import { computeSetupFingerprint } from '../setupRevision/setupFingerprint';
import type {
  ConnectorDocumentIdentity,
  ConnectorTargetRequest,
  ConnectorTargetSelection,
} from './connectorTargetCheckpoint';

export interface ConnectorTargetIdentityInput {
  projectKey: string | null | undefined;
  documentName: string | null | undefined;
  documentPath: string | null | undefined;
}

export interface ConnectorTargetRequestInput extends ConnectorTargetIdentityInput {
  target: ConnectorTargetSelection | null;
  setupRevisionId: number;
  /** Setup values the connector mutation itself depends on. */
  setupFields?: Record<string, unknown> | null;
}

const trimmed = (value: string | null | undefined): string | null => {
  const normalized = value?.trim();
  return normalized ? normalized : null;
};

/**
 * Resolves the active document identity, or nothing at all: a partially known
 * identity would let a replacement run against the wrong document.
 */
export const resolveConnectorDocumentIdentity = (
  input: ConnectorTargetIdentityInput
): ConnectorDocumentIdentity | null => {
  const projectKey = trimmed(input.projectKey);
  const documentTitle = trimmed(input.documentName);
  const documentPath = trimmed(input.documentPath);

  if (!projectKey || !documentTitle || !documentPath) {
    return null;
  }

  return { projectKey, documentTitle, documentPath };
};

export const buildConnectorTargetRequest = (
  input: ConnectorTargetRequestInput
): ConnectorTargetRequest | null => {
  const identity = resolveConnectorDocumentIdentity(input);
  if (!identity || !input.target) {
    return null;
  }

  return {
    ...identity,
    target: input.target,
    setupRevisionId: input.setupRevisionId,
    setupFingerprint: computeSetupFingerprint({
      identity,
      parameterDestination: input.target.destination,
      parameterBindingKind: input.target.bindingKind,
      setupFields: input.setupFields ?? null,
    }),
  };
};
